<template>
    <v-card
        flat
    >
        <v-list>
            <v-list-item>
                <v-list-item-content class="pa-2">
                    <div class="slider-labeling pt-3 pb-1">
                        <span class="float-left">
                            <generic-tooltip
                                v-if="tooltip"
                                style="align-items: flex-start"
                                :title="label"
                            >
                                <v-card
                                    tile
                                    width="400"
                                >
                                    <v-card-text
                                        class="pt-5"
                                        v-html="tooltip"
                                    />
                                </v-card>
                            </generic-tooltip>
                            <span
                                v-else
                                style="font-size: 14px"
                            >{{ label }}</span>
                        </span>
                        <span class="float-right">
                            {{ level }}
                        </span>
                    </div>
                    <vue-slider
                        v-model="internal"
                        :adsorb="false"
                        :clickable="!readonly"
                        :disabled="disabled"
                        :dot-options="dotOptions"
                        :dotSize="14"
                        :height="2"
                        :marks="marks"
                        :min="criteria.min"
                        :max="criteria.max"
                        tooltip="none"
                        contained
                        included
                        :mark-style="{ zIndex: 999 }"
                    >
                        <!-- eslint-disable-next-line no-unused-vars -->
                        <template v-slot:mark="slotProps">
                            <div
                                :key="slotProps.value"
                                :style="getMarkStyle(slotProps)"
                                :class="['vue-slider-mark', { 'vue-slider-mark-active': slotProps.value <= internal }]"
                            >
                                <v-tooltip
                                    top
                                    color="primary"
                                >
                                    <template v-slot:activator="{ on }">
                                        <div
                                            class="mark-hover-area"
                                            v-on="on"
                                        >
                                            <div :class="['vue-slider-mark-step', { 'vue-slider-mark-step-active': slotProps.value <= internal }]"></div>
                                        </div>
                                    </template>
                                    <span>{{ slotProps.label }}</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </vue-slider>
                </v-list-item-content>
                <v-list-item-action
                    v-if="criteria.nullable"
                >
                    <v-checkbox
                        class="no-info"
                        v-model="disabled"
                        :label="$t('noInformation')"
                    />
                </v-list-item-action>
            </v-list-item>
        </v-list>
    </v-card>
</template>

<script>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import VueSlider from 'vue-slider-component'
import GenericTooltip from '../tooltip'

const HUNDRED_FREE = 'hundred_free'

@Component({
    components: {
        GenericTooltip,
        VueSlider
    }
})
export default class RatingElement extends Vue {
    @Prop({ type: [String, Number] }) value

    @Prop({
        type: Object,
        required: true
    }) criteria

    @Prop({
        type: Boolean,
        default: () => false
    }) readonly

    @Prop({
        type: String,
        default: () => null
    }) id

    state = null

    get disabled () {
        return this.value === null
    }

    set disabled (value) {
        if (value) {
            this.state = this.value
            this.$emit('input', null)
        } else {
            this.$emit('input', this.state === null ? 1 : this.state)
            this.state = null
        }
    }

    get internal () {
        return this.value
    }

    set internal (value) {
        this.$emit('input', value)
    }

    get label () {
        return this.criteria.title
    }

    get tooltip () {
        return this.criteria.description
    }

    get options () {
        return this.criteria.labels
    }

    get marks () {
        if (this.criteria.marks === 0) {
            return {}
        }
        const delta = (this.criteria.max - this.criteria.min) / (this.criteria.marks - 1)
        const labels = this.criteria.labels
        const result = {}
        let index = 0
        for (let i = this.criteria.min; i <= this.criteria.max; i += delta) {
            result[i] = labels[index]
            index++
        }

        return result
    }

    get level () {
        return this.criteria.scale === HUNDRED_FREE
            ? this.levelAbsolute
            : this.levelIntermediate
    }

    get levelIntermediate () {
        const range = this.criteria.max - this.criteria.min
        const value = this.value - this.criteria.min
        const percent = value / range
        const idx = Math.round(percent * (this.options.length - 1))
        return this.options[idx]
    }

    get levelAbsolute () {
        return this.value === null || !this.options.length || this.options.length === 0 || this.criteria.max === 0
            ? ''
            : this.options[Math.ceil(this.value / (this.criteria.max / this.options.length)) - 1]
    }

    get dotOptions () {
        if (this.internal === undefined) {
            return [{ style: { display: 'none' } }]
        }

        return [{
            disabled: this.readonly
        }]
    }

    getMarkStyle (slotProps) {
        const pos = slotProps.pos
        // Handle edge cases for proper positioning
        if (pos === 0) {
            return { left: '0%', transform: 'translateX(0) translateY(0)' }
        } else if (pos === 100) {
            return { left: '100%', transform: 'translateX(-100%) translateY(0)' }
        } else {
            return slotProps.style || { left: `${pos}%`, transform: 'translateX(-50%) translateY(0)' }
        }
    }

    @Watch('id')
    onChangeId () {
        this.state = null
    }
}
</script>

<style lang="sass">
.v-list-item__action
    // width: 90px
    margin-left: 10px

.v-label
    font-size: 14px
    padding-left: 10px

.slider-wrapper
    padding: 0 5px
    position: relative

.mark-hover-area
    position: relative
    width: 30px
    height: 30px
    display: flex
    justify-content: center
    align-items: center
    cursor: pointer
    transform: translate(-15px, -15px)

.vue-slider-mark-step
    width: 2px
    height: 16px
    display: block !important
    visibility: visible !important
    background-color: currentColor
    left: auto !important
    transform: translate(1px, 7px)
    /* Moved down to align with slider track */
    pointer-events: none

.vue-slider-mark-step
    background: rgba(0, 0, 0, .16)

.vue-slider-mark-step-active
    background: rgb(81, 80, 97)

.vue-slider-dot
    margin-left: 1px

.no-info
    padding-top: 34px !important
</style>

<i18n>
{
    "de": {
        "noInformation": "Keine Angabe"
    },
    "en": {
        "noInformation": "No information"
    }
}
</i18n>
